import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Smartphone, Tablet, Monitor, WifiOff, Cloud, RefreshCw as Sync, RefreshCw, CheckCircle, AlertCircle, Info, Settings, Search, SortAsc, SortDesc, Grid, List, Trash2, FileText, RefreshCw as RefreshCwIcon, FileText as FileTextIcon, Cloud as CloudIcon } from 'lucide-react';

interface Device {
  id: string;
  name: string;
  type: 'desktop' | 'tablet' | 'mobile';
  platform: 'windows' | 'macos' | 'linux' | 'ios' | 'android' | 'web';
  lastSeen: Date;
  isOnline: boolean;
  isCurrent: boolean;
  syncStatus: 'synced' | 'pending' | 'error' | 'offline';
  lastSync: Date;
  version: string;
  capabilities: string[];
}

interface SyncData {
  id: string;
  type: 'bookmark' | 'highlight' | 'annotation' | 'progress' | 'settings' | 'preference';
  content: any;
  deviceId: string;
  timestamp: Date;
  syncStatus: 'pending' | 'synced' | 'conflict' | 'error';
  version: number;
  checksum: string;
}

interface CrossDeviceSyncProps {
  onSyncRequest?: (deviceId: string) => void;
  onDataSync?: (data: SyncData) => void;
  onDeviceConnect?: (device: Device) => void;
  onDeviceDisconnect?: (deviceId: string) => void;
}

const CrossDeviceSync: React.FC<CrossDeviceSyncProps> = ({
  onSyncRequest,
  onDataSync,
  onDeviceConnect,
  onDeviceDisconnect
}) => {
  const [devices, setDevices] = useState<Device[]>([]);
  const [syncData, setSyncData] = useState<SyncData[]>([]);
  const [selectedDevices, setSelectedDevices] = useState<Set<string>>(new Set());
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');
  const [filterStatus, setFilterStatus] = useState<string>('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [sortBy, setSortBy] = useState<'name' | 'lastSeen' | 'lastSync' | 'type'>('lastSeen');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showSettings, setShowSettings] = useState(false);
  const [autoSync, setAutoSync] = useState(true);
  const [syncInterval, setSyncInterval] = useState(30); // seconds
  const [conflictResolution, setConflictResolution] = useState<'latest' | 'manual' | 'device-priority'>('latest');
  const [showSyncModal, setShowSyncModal] = useState(false);
  const [syncProgress, setSyncProgress] = useState(0);

  const syncIntervalRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    loadDevices();
    loadSyncData();
    setupAutoSync();

    return () => {
      if (syncIntervalRef.current) {
        clearInterval(syncIntervalRef.current);
      }
    };
  }, [autoSync, syncInterval]);

  const loadDevices = async () => {
    try {
      setIsLoading(true);
      // In a real app, this would load from an API
      const sampleDevices: Device[] = [
        {
          id: '1',
          name: 'MacBook Pro',
          type: 'desktop',
          platform: 'macos',
          lastSeen: new Date('2024-01-20T10:30:00'),
          isOnline: true,
          isCurrent: true,
          syncStatus: 'synced',
          lastSync: new Date('2024-01-20T10:25:00'),
          version: '1.0.0',
          capabilities: ['bookmarks', 'highlights', 'annotations', 'progress', 'settings']
        },
        {
          id: '2',
          name: 'iPhone 15',
          type: 'mobile',
          platform: 'ios',
          lastSeen: new Date('2024-01-20T09:45:00'),
          isOnline: true,
          isCurrent: false,
          syncStatus: 'pending',
          lastSync: new Date('2024-01-20T09:40:00'),
          version: '1.0.0',
          capabilities: ['bookmarks', 'highlights', 'progress']
        },
        {
          id: '3',
          name: 'iPad Air',
          type: 'tablet',
          platform: 'ios',
          lastSeen: new Date('2024-01-19T16:20:00'),
          isOnline: false,
          isCurrent: false,
          syncStatus: 'offline',
          lastSync: new Date('2024-01-19T16:15:00'),
          version: '1.0.0',
          capabilities: ['bookmarks', 'highlights', 'annotations', 'progress']
        },
        {
          id: '4',
          name: 'Windows PC',
          type: 'desktop',
          platform: 'windows',
          lastSeen: new Date('2024-01-18T14:10:00'),
          isOnline: false,
          isCurrent: false,
          syncStatus: 'offline',
          lastSync: new Date('2024-01-18T14:05:00'),
          version: '1.0.0',
          capabilities: ['bookmarks', 'highlights', 'annotations', 'progress', 'settings']
        }
      ];
      setDevices(sampleDevices);
    } catch (err) {
      setError('Failed to load devices');
    } finally {
      setIsLoading(false);
    }
  };

  const loadSyncData = async () => {
    try {
      // In a real app, this would load from an API
      const sampleSyncData: SyncData[] = [
        {
          id: '1',
          type: 'bookmark',
          content: { articleId: '123', title: 'Sample Article' },
          deviceId: '1',
          timestamp: new Date('2024-01-20T10:25:00'),
          syncStatus: 'synced',
          version: 1,
          checksum: 'abc123'
        },
        {
          id: '2',
          type: 'highlight',
          content: { articleId: '123', text: 'Important text', color: 'yellow' },
          deviceId: '2',
          timestamp: new Date('2024-01-20T09:45:00'),
          syncStatus: 'pending',
          version: 1,
          checksum: 'def456'
        },
        {
          id: '3',
          type: 'progress',
          content: { articleId: '123', progress: 0.75, lastPosition: 1200 },
          deviceId: '1',
          timestamp: new Date('2024-01-20T10:20:00'),
          syncStatus: 'synced',
          version: 1,
          checksum: 'ghi789'
        }
      ];
      setSyncData(sampleSyncData);
    } catch (err) {
      setError('Failed to load sync data');
    }
  };

  const setupAutoSync = () => {
    if (syncIntervalRef.current) {
      clearInterval(syncIntervalRef.current);
    }

    if (autoSync) {
      syncIntervalRef.current = setInterval(() => {
        syncAllDevices();
      }, syncInterval * 1000);
    }
  };

  const syncAllDevices = async () => {
    try {
      setIsLoading(true);
      setSyncProgress(0);
      
      const onlineDevices = devices.filter(device => device.isOnline && !device.isCurrent);
      
      for (let i = 0; i < onlineDevices.length; i++) {
        const device = onlineDevices[i];
        await syncDevice(device.id);
        setSyncProgress(((i + 1) / onlineDevices.length) * 100);
      }
      
      setSyncProgress(100);
      setTimeout(() => setSyncProgress(0), 2000);
      
    } catch (err) {
      setError('Failed to sync devices');
    } finally {
      setIsLoading(false);
    }
  };

  const syncDevice = async (deviceId: string) => {
    try {
      // Simulate sync process
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      setDevices(prev => 
        prev.map(device => 
          device.id === deviceId 
            ? { ...device, syncStatus: 'synced' as const, lastSync: new Date() }
            : device
        )
      );
      
      onSyncRequest?.(deviceId);
      
    } catch (err) {
      setDevices(prev => 
        prev.map(device => 
          device.id === deviceId 
            ? { ...device, syncStatus: 'error' as const }
            : device
        )
      );
    }
  };

  const handleDeviceSelect = (deviceId: string) => {
    setSelectedDevices(prev => {
      const newSet = new Set(prev);
      if (newSet.has(deviceId)) {
        newSet.delete(deviceId);
      } else {
        newSet.add(deviceId);
      }
      return newSet;
    });
  };

  const handleSelectAll = () => {
    if (selectedDevices.size === filteredDevices.length) {
      setSelectedDevices(new Set());
    } else {
      setSelectedDevices(new Set(filteredDevices.map(device => device.id)));
    }
  };

  const handleBulkSync = async () => {
    const deviceIds = Array.from(selectedDevices);
    for (const deviceId of deviceIds) {
      await syncDevice(deviceId);
    }
    setSelectedDevices(new Set());
  };

  const handleDeviceDisconnect = (deviceId: string) => {
    setDevices(prev => prev.filter(device => device.id !== deviceId));
    onDeviceDisconnect?.(deviceId);
  };

  const formatDate = (date: Date): string => {
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getDeviceIcon = (type: string) => {
    switch (type) {
      case 'desktop': return Monitor;
      case 'tablet': return Tablet;
      case 'mobile': return Smartphone;
      default: return Monitor;
    }
  };

  const getSyncStatusIcon = (status: string) => {
    switch (status) {
      case 'synced': return CheckCircle;
      case 'pending': return RefreshCw;
      case 'error': return AlertCircle;
      case 'offline': return WifiOff;
      default: return Info;
    }
  };

  const getSyncStatusColor = (status: string) => {
    switch (status) {
      case 'synced': return 'text-green-600 dark:text-green-400';
      case 'pending': return 'text-yellow-600 dark:text-yellow-400';
      case 'error': return 'text-red-600 dark:text-red-400';
      case 'offline': return 'text-gray-600 dark:text-gray-400';
      default: return 'text-gray-600 dark:text-gray-400';
    }
  };

  const filteredDevices = devices
    .filter(device => {
      const matchesStatus = filterStatus === 'all' || device.syncStatus === filterStatus;
      const matchesSearch = device.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
                           device.platform.toLowerCase().includes(searchQuery.toLowerCase());
      
      return matchesStatus && matchesSearch;
    })
    .sort((a, b) => {
      let aValue: any, bValue: any;
      
      switch (sortBy) {
        case 'name':
          aValue = a.name.toLowerCase();
          bValue = b.name.toLowerCase();
          break;
        case 'lastSeen':
          aValue = a.lastSeen.getTime();
          bValue = b.lastSeen.getTime();
          break;
        case 'lastSync':
          aValue = a.lastSync.getTime();
          bValue = b.lastSync.getTime();
          break;
        case 'type':
          aValue = a.type;
          bValue = b.type;
          break;
        default:
          return 0;
      }
      
      if (sortOrder === 'asc') {
        return aValue < bValue ? -1 : aValue > bValue ? 1 : 0;
      } else {
        return aValue > bValue ? -1 : aValue < bValue ? 1 : 0;
      }
    });

  if (isLoading && devices.length === 0) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center p-8">
        <div className="text-red-500 mb-4">{error}</div>
        <button 
          onClick={() => window.location.reload()}
          className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600"
        >
          Retry
        </button>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto p-6">
      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-2">
              Cross-Device Sync
            </h1>
            <p className="text-gray-600 dark:text-gray-400">
              Synchronize your reading progress and preferences across all devices
            </p>
          </div>
          
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-blue-100 dark:bg-blue-900 text-blue-700 dark:text-blue-300">
              <Cloud className="h-4 w-4" />
              <span className="text-sm font-medium">
                {devices.filter(d => d.isOnline).length} Online
              </span>
            </div>
            
            <button
              onClick={() => setShowSettings(!showSettings)}
              className="p-2 text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white"
            >
              <Settings className="h-5 w-5" />
            </button>
          </div>
        </div>
      </div>

      {/* Sync Progress */}
      {syncProgress > 0 && (
        <div className="bg-white dark:bg-gray-800 rounded-lg p-6 shadow-sm border border-gray-200 dark:border-gray-700 mb-6">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
              Syncing devices...
            </span>
            <span className="text-sm text-gray-600 dark:text-gray-400">
              {Math.round(syncProgress)}%
            </span>
          </div>
          <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2">
            <div
              className="bg-blue-500 h-2 rounded-full transition-all duration-300"
              style={{ width: `${syncProgress}%` }}
            />
          </div>
        </div>
      )}

      {/* Controls */}
      <div className="bg-white dark:bg-gray-800 rounded-lg p-6 shadow-sm border border-gray-200 dark:border-gray-700 mb-6">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
              <input
                type="text"
                placeholder="Search devices..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>

            <select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
              className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="all">All Status</option>
              <option value="synced">Synced</option>
              <option value="pending">Pending</option>
              <option value="error">Error</option>
              <option value="offline">Offline</option>
            </select>

            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value as any)}
              className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="lastSeen">Last Seen</option>
              <option value="lastSync">Last Sync</option>
              <option value="name">Name</option>
              <option value="type">Type</option>
            </select>

            <button
              onClick={() => setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc')}
              className="p-2 text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white"
            >
              {sortOrder === 'asc' ? <SortAsc className="h-4 w-4" /> : <SortDesc className="h-4 w-4" />}
            </button>
          </div>

          <div className="flex items-center gap-4">
            <div className="flex border border-gray-300 dark:border-gray-600 rounded-lg">
              <button
                onClick={() => setViewMode('grid')}
                className={`p-2 ${viewMode === 'grid' ? 'bg-blue-500 text-white' : 'bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-300'}`}
              >
                <Grid className="h-4 w-4" />
              </button>
              <button
                onClick={() => setViewMode('list')}
                className={`p-2 ${viewMode === 'list' ? 'bg-blue-500 text-white' : 'bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-300'}`}
              >
                <List className="h-4 w-4" />
              </button>
            </div>

            {selectedDevices.size > 0 && (
              <button
                onClick={handleBulkSync}
                className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 flex items-center"
              >
                <Sync className="h-4 w-4 mr-2" />
                Sync ({selectedDevices.size})
              </button>
            )}

            <button
              onClick={syncAllDevices}
              className="px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 flex items-center"
            >
              <RefreshCw className="h-4 w-4 mr-2" />
              Sync All
            </button>
          </div>
        </div>
      </div>

      {/* Devices Grid/List */}
      {viewMode === 'grid' ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredDevices.map((device) => {
            const DeviceIcon = getDeviceIcon(device.type);
            const SyncStatusIcon = getSyncStatusIcon(device.syncStatus);
            
            return (
              <div
                key={device.id}
                className={`bg-white dark:bg-gray-800 rounded-lg p-6 shadow-sm border border-gray-200 dark:border-gray-700 hover:shadow-md transition-shadow ${
                  device.isCurrent ? 'ring-2 ring-blue-500 bg-blue-50 dark:bg-blue-900' : ''
                }`}
              >
                <div className="flex items-start justify-between mb-4">
                  <div className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      checked={selectedDevices.has(device.id)}
                      onChange={() => handleDeviceSelect(device.id)}
                      className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                    />
                    <DeviceIcon className="h-6 w-6 text-gray-600 dark:text-gray-400" />
                  </div>
                  
                  <div className="flex items-center gap-2">
                    <SyncStatusIcon className={`h-4 w-4 ${getSyncStatusColor(device.syncStatus)}`} />
                    <span className={`text-xs font-medium ${getSyncStatusColor(device.syncStatus)}`}>
                      {device.syncStatus}
                    </span>
                  </div>
                </div>

                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-1">
                  {device.name}
                </h3>
                <p className="text-gray-600 dark:text-gray-400 mb-4 capitalize">
                  {device.platform} • {device.type}
                </p>

                <div className="space-y-2 mb-4">
                  <div className="flex justify-between text-sm text-gray-500 dark:text-gray-400">
                    <span>Last seen</span>
                    <span>{formatDate(device.lastSeen)}</span>
                  </div>
                  <div className="flex justify-between text-sm text-gray-500 dark:text-gray-400">
                    <span>Last sync</span>
                    <span>{formatDate(device.lastSync)}</span>
                  </div>
                  <div className="flex justify-between text-sm text-gray-500 dark:text-gray-400">
                    <span>Version</span>
                    <span>{device.version}</span>
                  </div>
                </div>

                <div className="flex items-center justify-between">
                  <div className="text-sm text-gray-500 dark:text-gray-400">
                    {device.capabilities.length} capabilities
                  </div>
                  
                  <div className="flex items-center gap-2">
                    {!device.isCurrent && (
                      <button
                        onClick={() => syncDevice(device.id)}
                        className="p-2 text-blue-600 hover:text-blue-700"
                        title="Sync device"
                      >
                        <Sync className="h-4 w-4" />
                      </button>
                    )}
                    {!device.isCurrent && (
                      <button
                        onClick={() => handleDeviceDisconnect(device.id)}
                        className="p-2 text-red-600 hover:text-red-700"
                        title="Disconnect device"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        <div className="space-y-4">
          {filteredDevices.map((device) => {
            const DeviceIcon = getDeviceIcon(device.type);
            const SyncStatusIcon = getSyncStatusIcon(device.syncStatus);
            
            return (
              <div
                key={device.id}
                className={`bg-white dark:bg-gray-800 rounded-lg p-6 shadow-sm border border-gray-200 dark:border-gray-700 ${
                  device.isCurrent ? 'ring-2 ring-blue-500 bg-blue-50 dark:bg-blue-900' : ''
                }`}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-4">
                    <input
                      type="checkbox"
                      checked={selectedDevices.has(device.id)}
                      onChange={() => handleDeviceSelect(device.id)}
                      className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                    />
                    <DeviceIcon className="h-6 w-6 text-gray-600 dark:text-gray-400" />
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                        {device.name}
                      </h3>
                      <p className="text-gray-600 dark:text-gray-400 capitalize">
                        {device.platform} • {device.type}
                      </p>
                    </div>
                  </div>

                  <div className="text-right">
                    <div className="flex items-center gap-2 mb-2">
                      <SyncStatusIcon className={`h-4 w-4 ${getSyncStatusColor(device.syncStatus)}`} />
                      <span className={`text-xs font-medium ${getSyncStatusColor(device.syncStatus)}`}>
                        {device.syncStatus}
                      </span>
                    </div>
                    <div className="text-sm text-gray-600 dark:text-gray-400">
                      Last seen: {formatDate(device.lastSeen)}
                    </div>
                    <div className="text-sm text-gray-500 dark:text-gray-500">
                      Last sync: {formatDate(device.lastSync)}
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {filteredDevices.length === 0 && (
        <div className="text-center py-12">
          <Cloud className="h-12 w-12 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
            No devices found
          </h3>
          <p className="text-gray-600 dark:text-gray-400">
            Try adjusting your search or filter criteria
          </p>
        </div>
      )}

      {/* Settings Modal */}
      {showSettings && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg p-6 w-full max-w-md">
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">
              Sync Settings
            </h2>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Auto-sync enabled
                </label>
                <input
                  type="checkbox"
                  checked={autoSync}
                  onChange={(e) => setAutoSync(e.target.checked)}
                  className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Sync interval: {syncInterval} seconds
                </label>
                <input
                  type="range"
                  min="10"
                  max="300"
                  step="10"
                  value={syncInterval}
                  onChange={(e) => setSyncInterval(parseInt(e.target.value))}
                  className="w-full"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Conflict resolution
                </label>
                <select
                  value={conflictResolution}
                  onChange={(e) => setConflictResolution(e.target.value as any)}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="latest">Use latest version</option>
                  <option value="manual">Manual resolution</option>
                  <option value="device-priority">Device priority</option>
                </select>
              </div>
            </div>

            <div className="flex justify-end gap-4 mt-6">
              <button
                onClick={() => setShowSettings(false)}
                className="px-4 py-2 bg-gray-500 text-white rounded-lg hover:bg-gray-600"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CrossDeviceSync;
